import type { InvitationData } from '../types';

export interface HappyMember {
  name: string;
  photoUrl?: string;
  emoji?: string;
}

export interface ScheduleItem {
  time: string;
  activity: string;
  venue?: string;
  description?: string;
  emoji?: string;
}

export interface BirthdayContent {
  hero: {
    birthdayPersonName: string;
    tagline: string;
    eventDate: string;
    hostName: string;
    mainPhotoUrl?: string;
  };
  photoTrio: {
    photos: string[];
  };
  happyMembers: {
    label: string;
    title: string;
    members: HappyMember[];
  };
  schedule: {
    label: string;
    title: string;
    items: ScheduleItem[];
  };
  venue: {
    name: string;
    address: string;
    mapsLink?: string;
  };
  rsvp: {
    link?: string;
    message?: string;
  };
}

import { parseFlexibleDate } from '@/src/infrastructure/lib/weddingDataHelper';

function fmt(iso: string | undefined): string {
  if (!iso) return '';
  const d = parseFlexibleDate(iso);
  if (!d) return iso;
  try {
    return new Intl.DateTimeFormat('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
}

const DEFAULT_MEMBERS: HappyMember[] = [
  { name: 'Rahul', emoji: '🎉' },
  { name: 'Priya', emoji: '🎂' },
  { name: 'Amit', emoji: '🥳' },
  { name: 'Neha', emoji: '🎈' },
  { name: 'Ravi', emoji: '🎊' },
  { name: 'Sunita', emoji: '🎁' },
];

export function resolveContent(data: InvitationData): BirthdayContent {
  const bd = (data.customData as Record<string, unknown>)?.birthday as Record<string, unknown> | undefined;

  const rawMembers = bd?.happyMembers as HappyMember[] | undefined;
  const rawSchedule = bd?.schedule as ScheduleItem[] | undefined;
  const birthdayPersonName = (bd?.birthdayPersonName as string | undefined) ?? data.eventName;

  const customDataAny = (data.customData as Record<string, any>) || {};
  const allMedia = data.mediaUrls ?? [];
  const [firstMedia, ...rest] = allMedia;
  const main = firstMedia || customDataAny.heroImageUrl || customDataAny.photoUrl || (bd?.photo as string | undefined);
  const trioPhotos = rest.slice(0, 3);

  const venue = data.eventVenue || 'Celebration Venue';
  const defaultSchedule: ScheduleItem[] = [
    { time: '5:00 PM', activity: 'Arrive & Mingle', venue, emoji: '🚪' },
    { time: '7:00 PM', activity: 'Evening Snacks & Music', venue, emoji: '🍿' },
    { time: '9:00 PM', activity: 'Dinner & Felicitations', venue, emoji: '🍽️' },
    { time: '10:30 PM', activity: 'Celebration & Dance', venue, emoji: '💃' },
  ];

  return {
    hero: {
      birthdayPersonName,
      tagline: `Celebrating ${birthdayPersonName}'s Special Day`,
      eventDate: fmt(data.eventDate),
      hostName: data.hostName,
      mainPhotoUrl: main,
    },
    photoTrio: {
      photos: trioPhotos,
    },
    happyMembers: {
      label: 'The Squad',
      title: 'Happy Celebrants',
      members: rawMembers?.length ? rawMembers : DEFAULT_MEMBERS,
    },
    schedule: {
      label: 'The Plan',
      title: 'Activity Schedule',
      items: rawSchedule?.length ? rawSchedule : defaultSchedule,
    },
    venue: {
      name: data.eventVenue,
      address: (bd?.venueAddress as string | undefined) ?? data.eventVenue,
      mapsLink: bd?.mapsLink as string | undefined,
    },
    rsvp: {
      link: data.rsvpLink,
      message: data.customMessage,
    },
  };
}
